// src/features/userprofile/MediaAdminPanel.tsx
// Admin media repair tools — avatars/banners don't get re-uploaded when a URL
// or thumbnail breaks, an admin fixes the record directly. Also covers the
// bulk provisioning/cleanup RPCs that only make sense from an admin console.
//
// DeleteAvatarsBulk's generated store method drops its own request body (a
// codegen gap, same class as resource-ia.md's UnlinkResources) — fixed at the
// call site by passing a manual JSON body through RequestOptions.

import { useState } from 'react';
import { observer } from '@react';
import { useUserprofileAvatar, useUserprofileBanner, useI18n } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Alert, ErrorAlert } from '@react/components';
import { ToggleSwitch } from '../../components/ToggleSwitch';
import { DeleteAvatarsBulk } from '@core/api/endpoints/userprofile/delete_avatars_bulk';
import type { Avatar, Banner } from '@core/types/userprofile';

const DELETE_KEYWORD = 'DELETE';

export const MediaAdminPanel = observer(() => {
  const avatarStore = useUserprofileAvatar();
  const bannerStore = useUserprofileBanner();
  const { t } = useI18n();

  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // --- Avatars by owner ---
  const [ownerId, setOwnerId] = useState('');
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [avatarsLoading, setAvatarsLoading] = useState(false);
  const [editingAvatar, setEditingAvatar] = useState<Avatar | null>(null);
  const [avatarForm, setAvatarForm] = useState({ url: '', thumbnail_url: '', original_filename: '', content_type: '' });

  const handleLoadAvatarsByOwner = async () => {
    if (!ownerId.trim()) return;
    setAvatarsLoading(true);
    setError(null);
    try {
      await avatarStore.ListAvatarsByOwner(ownerId.trim(), { pagination: { limit: 50, cursor: '', direction: '' } } as never);
      setAvatars([...avatarStore.items]);
    } catch (err) {
      setError(err);
    } finally {
      setAvatarsLoading(false);
    }
  };

  const openAvatarEdit = async (avatar: Avatar) => {
    setError(null);
    try {
      // Re-fetch the single record so edits start from the freshest copy.
      const fresh = await avatarStore.GetAvatar(ownerId.trim(), avatar.entity?.id ?? '');
      setEditingAvatar(fresh);
      setAvatarForm({
        url: fresh.url,
        thumbnail_url: fresh.thumbnail_url,
        original_filename: fresh.original_filename,
        content_type: fresh.content_type,
      });
    } catch (err) {
      setError(err);
    }
  };

  const handleSaveAvatar = async () => {
    if (!editingAvatar) return;
    try {
      await avatarStore.UpdateAvatar(ownerId.trim(), editingAvatar.entity?.id ?? '', {
        avatar: { ...editingAvatar, ...avatarForm },
      });
      setSuccess(t('userprofile.admin.avatarUpdated'));
      setEditingAvatar(null);
      handleLoadAvatarsByOwner();
    } catch (err) {
      setError(err);
    }
  };

  // --- Banner lookup ---
  const [bannerProfileId, setBannerProfileId] = useState('');
  const [bannerId, setBannerId] = useState('');
  const [banner, setBanner] = useState<Banner | null>(null);
  const [bannerForm, setBannerForm] = useState({ url: '', thumbnail_url: '', original_filename: '', content_type: '' });
  const [bannerLoading, setBannerLoading] = useState(false);

  const handleLoadBanner = async () => {
    if (!bannerProfileId.trim() || !bannerId.trim()) return;
    setBannerLoading(true);
    setError(null);
    try {
      const resp = await bannerStore.GetBanner(bannerProfileId.trim(), bannerId.trim());
      setBanner(resp);
      setBannerForm({
        url: resp.url,
        thumbnail_url: resp.thumbnail_url,
        original_filename: resp.original_filename,
        content_type: resp.content_type,
      });
    } catch (err) {
      setError(err);
    } finally {
      setBannerLoading(false);
    }
  };

  const handleSaveBanner = async () => {
    if (!banner) return;
    try {
      await bannerStore.UpdateBanner(bannerProfileId.trim(), bannerId.trim(), {
        banner: { ...banner, ...bannerForm },
      });
      setSuccess(t('userprofile.admin.bannerUpdated'));
      handleLoadBanner();
    } catch (err) {
      setError(err);
    }
  };

  // --- Bulk import ---
  type ImportRow = { ownerId: string; url: string; filename: string };
  const [importRows, setImportRows] = useState<ImportRow[]>([{ ownerId: '', url: '', filename: '' }]);
  const [importing, setImporting] = useState(false);

  const addImportRow = () => setImportRows((prev) => [...prev, { ownerId: '', url: '', filename: '' }]);
  const removeImportRow = (i: number) => setImportRows((prev) => prev.filter((_, idx) => idx !== i));
  const updateImportRow = (i: number, field: keyof ImportRow, value: string) =>
    setImportRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  const handleBulkImport = async () => {
    const rows = importRows.filter((r) => r.ownerId.trim() && r.url.trim());
    if (rows.length === 0) return;
    setImporting(true);
    setError(null);
    try {
      const resp = await avatarStore.CreateAvatarsBulk({
        avatars: {
          avatars: rows.map((r) => ({
            entity: {} as never,
            owner: { id: r.ownerId.trim() } as never,
            url: r.url.trim(),
            thumbnail_url: '',
            original_filename: r.filename.trim(),
            content_type: '',
            size_bytes: 0,
            is_active: false,
          })),
        },
      });
      setSuccess(t('userprofile.admin.bulkImportResult', { created: resp.created_avatars?.avatars?.length ?? 0, failed: resp.failed_ids?.length ?? 0 }));
      setImportRows([{ ownerId: '', url: '', filename: '' }]);
    } catch (err) {
      setError(err);
    } finally {
      setImporting(false);
    }
  };

  // --- Bulk delete ---
  const [bulkIds, setBulkIds] = useState('');
  const [hardDelete, setHardDelete] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkConfirmText, setBulkConfirmText] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const handleBulkDelete = async () => {
    const ids = bulkIds.split('\n').map((s) => s.trim()).filter(Boolean);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    setError(null);
    try {
      // Store method drops params — call the raw endpoint with an explicit body.
      const resp = await DeleteAvatarsBulk({
        body: JSON.stringify({ parent: {}, avatar_ids: ids, hard: hardDelete }),
      });
      setSuccess(t('userprofile.admin.bulkDeleteResult', { deleted: resp.deleted_ids?.length ?? 0, failed: resp.failed_ids?.length ?? 0 }));
      setBulkIds('');
      setBulkConfirmOpen(false);
      setBulkConfirmText('');
    } catch (err) {
      setError(err);
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      {!!error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
      {success && <Alert variant="success" dismissible onDismiss={() => setSuccess(null)}>{success}</Alert>}

      {/* Avatars by owner */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('userprofile.admin.avatarsByOwnerTitle')}</CardTitle>
          <CardDescription>{t('userprofile.admin.avatarsByOwnerDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2 items-end">
            <Input label={t('userprofile.admin.ownerProfileId')} value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className="flex-1" />
            <Button onClick={handleLoadAvatarsByOwner} isLoading={avatarsLoading} disabled={!ownerId.trim()}>
              {t('common.load')}
            </Button>
          </div>

          {avatars.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {avatars.map((a) => (
                <button
                  key={a.entity?.id}
                  type="button"
                  onClick={() => openAvatarEdit(a)}
                  className="text-left p-2 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <div className="aspect-square rounded bg-muted overflow-hidden mb-1">
                    {a.thumbnail_url || a.url ? (
                      <img src={a.thumbnail_url || a.url} alt="" className="w-full h-full object-cover" />
                    ) : null}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">{a.original_filename || a.entity?.id?.slice(0, 8)}</p>
                </button>
              ))}
            </div>
          )}

          {editingAvatar && (
            <div className="rounded-lg border border-border p-4 space-y-3">
              <p className="text-sm font-medium text-foreground">{t('userprofile.admin.editAvatarRecord')}</p>
              <Input label={t('userprofile.admin.fileUrl')} value={avatarForm.url} onChange={(e) => setAvatarForm((f) => ({ ...f, url: e.target.value }))} />
              <Input label={t('userprofile.admin.thumbnailUrl')} value={avatarForm.thumbnail_url} onChange={(e) => setAvatarForm((f) => ({ ...f, thumbnail_url: e.target.value }))} />
              <Input label={t('userprofile.admin.filename')} value={avatarForm.original_filename} onChange={(e) => setAvatarForm((f) => ({ ...f, original_filename: e.target.value }))} />
              <Input label={t('userprofile.admin.contentType')} value={avatarForm.content_type} onChange={(e) => setAvatarForm((f) => ({ ...f, content_type: e.target.value }))} />
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => setEditingAvatar(null)}>{t('common.cancel')}</Button>
                <Button size="sm" onClick={handleSaveAvatar} isLoading={avatarStore.isSaving}>{t('common.save')}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Banner lookup */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('userprofile.admin.bannerLookupTitle')}</CardTitle>
          <CardDescription>{t('userprofile.admin.bannerLookupDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
            <Input label={t('userprofile.admin.ownerProfileId')} value={bannerProfileId} onChange={(e) => setBannerProfileId(e.target.value)} />
            <Input label={t('userprofile.admin.bannerId')} value={bannerId} onChange={(e) => setBannerId(e.target.value)} />
            <Button onClick={handleLoadBanner} isLoading={bannerLoading} disabled={!bannerProfileId.trim() || !bannerId.trim()}>
              {t('common.load')}
            </Button>
          </div>

          {banner && (
            <div className="rounded-lg border border-border p-4 space-y-3">
              {banner.url && <img src={banner.url} alt="" className="h-20 rounded object-cover" />}
              <Input label={t('userprofile.admin.fileUrl')} value={bannerForm.url} onChange={(e) => setBannerForm((f) => ({ ...f, url: e.target.value }))} />
              <Input label={t('userprofile.admin.thumbnailUrl')} value={bannerForm.thumbnail_url} onChange={(e) => setBannerForm((f) => ({ ...f, thumbnail_url: e.target.value }))} />
              <Input label={t('userprofile.admin.filename')} value={bannerForm.original_filename} onChange={(e) => setBannerForm((f) => ({ ...f, original_filename: e.target.value }))} />
              <Input label={t('userprofile.admin.contentType')} value={bannerForm.content_type} onChange={(e) => setBannerForm((f) => ({ ...f, content_type: e.target.value }))} />
              <div className="flex justify-end">
                <Button size="sm" onClick={handleSaveBanner} isLoading={bannerStore.isSaving}>{t('common.save')}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk import */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('userprofile.admin.bulkImportTitle')}</CardTitle>
          <CardDescription>{t('userprofile.admin.bulkImportDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {importRows.map((row, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_1fr_auto] gap-2 items-end">
              <Input label={i === 0 ? t('userprofile.admin.ownerProfileId') : undefined} value={row.ownerId} onChange={(e) => updateImportRow(i, 'ownerId', e.target.value)} />
              <Input label={i === 0 ? t('userprofile.admin.fileUrl') : undefined} value={row.url} onChange={(e) => updateImportRow(i, 'url', e.target.value)} />
              <Input label={i === 0 ? t('userprofile.admin.filename') : undefined} value={row.filename} onChange={(e) => updateImportRow(i, 'filename', e.target.value)} />
              <Button variant="ghost" size="sm" onClick={() => removeImportRow(i)} className="shrink-0">&times;</Button>
            </div>
          ))}
          <Button variant="secondary" size="sm" onClick={addImportRow}>{t('userprofile.admin.addRow')}</Button>
        </CardContent>
        <CardFooter className="justify-end">
          <Button onClick={handleBulkImport} isLoading={importing}>{t('userprofile.admin.importAll')}</Button>
        </CardFooter>
      </Card>

      {/* Bulk delete */}
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="text-base">{t('userprofile.admin.bulkDeleteTitle')}</CardTitle>
          <CardDescription>{t('userprofile.admin.bulkDeleteDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input label={t('userprofile.admin.avatarIdsOneLine')} value={bulkIds} onChange={(e) => setBulkIds(e.target.value)} placeholder="id-1&#10;id-2" />
          <ToggleSwitch label={t('userprofile.admin.hardDelete')} description={t('userprofile.admin.hardDeleteDesc')} checked={hardDelete} onChange={setHardDelete} />
          {!bulkConfirmOpen ? (
            <div className="flex justify-end">
              <Button variant="danger" size="sm" onClick={() => setBulkConfirmOpen(true)} disabled={!bulkIds.trim()}>
                {t('userprofile.admin.deleteSelected')}
              </Button>
            </div>
          ) : (
            <div className="rounded-lg border border-destructive/50 p-3 space-y-2">
              <p className="text-sm text-muted-foreground">{t('security.logoutAllTypeToConfirm', { keyword: DELETE_KEYWORD })}</p>
              <input
                type="text"
                value={bulkConfirmText}
                onChange={(e) => setBulkConfirmText(e.target.value)}
                placeholder={DELETE_KEYWORD}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono"
                autoComplete="off"
              />
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => { setBulkConfirmOpen(false); setBulkConfirmText(''); }}>{t('common.cancel')}</Button>
                <Button variant="danger" size="sm" onClick={handleBulkDelete} isLoading={bulkDeleting} disabled={bulkConfirmText !== DELETE_KEYWORD}>
                  {t('userprofile.admin.deleteSelected')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default MediaAdminPanel;
